import { Box, Divider, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Alert from '/@/components/bayer/alert';
import RadioButtons from '/@/components/radio-buttons';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useCPCropStages, useCPSimplifiedUseRecommendations } from '/@/hooks/crop-protection';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { reverse } from '/@/utils/router';

const ProductRow = ({ useRecommendation }: { useRecommendation: any }) => {
    const navigate = useNavigate();
    return (
        <Tooltip
            title={
                _(useRecommendation?.note) || _(useRecommendation?.alert) ? (
                    <Stack spacing={2}>
                        {_(useRecommendation?.note) && <span>{_(useRecommendation?.note)}</span>}
                        {_(useRecommendation?.alert) && <Alert>{_(useRecommendation?.alert)}</Alert>}
                    </Stack>
                ) : (
                    ''
                )
            }
            placement='top'
            arrow
        >
            <Stack
                key={useRecommendation.product.name}
                id={`product-row-${useRecommendation.id}`}
                sx={{
                    border: 1,
                    borderColor: (theme) => theme.palette.divider,
                    background: (theme) => theme.palette.grey[100],
                    width: `${(WIDTH + 1) * (useRecommendation.toStageIndex - useRecommendation.fromStageIndex + 1) - 10}px`,
                    ml: `${(WIDTH + 1) * useRecommendation.fromStageIndex}px !important`,

                    '&:hover': {
                        boxShadow: (theme) => theme.shadows[1],
                        transition: 'box-shadow 0.1s ease-in-out',
                        cursor: 'pointer',
                        background: (theme) => theme.palette.grey[200],
                    },
                }}
                p={2}
                spacing={2}
                direction='row'
                alignItems='center'
                zIndex={1}
                onClick={() => {
                    navigate(
                        reverse('productExploration.detail', {
                            params: {
                                type: 'crop_protection',
                                slug: useRecommendation.product.brand.name.toLowerCase(),
                                productId: useRecommendation.product.id,
                            },
                        })
                    );
                }}
            >
                <Box component='img' src={useRecommendation.product.image} width={60} />
                {useRecommendation.toStageIndex - useRecommendation.fromStageIndex > 1 && (
                    <Stack>
                        <Typography variant='h5' color='primary'>
                            {useRecommendation.product.name}
                        </Typography>
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {_(useRecommendation.product.type.name)}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Tooltip>
    );
};

const WIDTH = 120;
const CropProtectionWhenToApply = ({
    productId,
    initialCrop,
    showCropProtectionProducts = true,
}: {
    productId?: number;
    initialCrop?: string;
    showCropProtectionProducts?: boolean;
}) => {
    const [crop, setCrop] = useState<string>(initialCrop || '');
    const [type, setType] = useState<string>('');

    const brands = useSelector((state) => getCatalogBrandsByCrop(state, 'crop_protection'));
    const cropStagesQuery = useCPCropStages({ page: 1, pageSize: 10000, crop: crop ?? undefined });
    const simplifiedUseRecommendationsQuery = useCPSimplifiedUseRecommendations({
        productId,
        page: 1,
        pageSize: 10000,
        brandIds: brands.map((brand) => brand.id),
    });
    const simplifiedUseRecommendations = useMemo(() => {
        const results = simplifiedUseRecommendationsQuery.data?.results || [];
        if (results.length > 0 && !initialCrop) {
            const firstResult = results[0];
            setCrop(firstResult?.crop.name.en || '');
            setType(_(firstResult?.product.type.name) || '');
        }
        return results || [];
    }, [simplifiedUseRecommendationsQuery.data]);

    const cropStages = useMemo(() => {
        if (cropStagesQuery.data) {
            return cropStagesQuery.data.results;
        }
        return [];
    }, [cropStagesQuery.data]);

    const cropOptions = useMemo<{ label: string; value: string }[]>(() => {
        const uniqueOptions = new Map();
        simplifiedUseRecommendations.forEach(({ crop }) => {
            uniqueOptions.set(_(crop.name), {
                label: _(crop.name),
                value: crop.name.en || '',
            });
        });
        return Array.from(uniqueOptions.values());
    }, [simplifiedUseRecommendations]);

    const typeOptions = useMemo<{ label: string; value: string }[]>(() => {
        const uniqueOptions = new Map();
        simplifiedUseRecommendations
            .filter((useRecommendation) => useRecommendation.crop.name?.en === crop)
            .forEach((useRecommendation) => {
                uniqueOptions.set(_(useRecommendation.product.type.name), {
                    label: _(useRecommendation.product.type.name),
                    value: _(useRecommendation.product.type.name),
                });
            });
        return Array.from(uniqueOptions.values());
    }, [crop, simplifiedUseRecommendations]);

    const filteredsimplifiedUseRecommendations = simplifiedUseRecommendations
        .filter((useRecommendation) => {
            const matchesCrop = useRecommendation.crop.name?.en?.toLowerCase() === crop?.toLowerCase();
            const matchesType = !type || type === _(useRecommendation.product.type.name);

            return matchesCrop && matchesType;
        })
        .map((useRecommendation) => {
            return {
                ...useRecommendation,
                fromStageIndex: cropStages.findIndex(
                    (cropStage) => _(cropStage.name) === _(useRecommendation.fromStage.name)
                ),
                toStageIndex: cropStages.findIndex(
                    (cropStage) => _(cropStage.name) === _(useRecommendation.toStage.name)
                ),
            };
        })
        .filter((useRecommendation) => useRecommendation.fromStageIndex !== -1 && useRecommendation.toStageIndex !== -1)
        .toSorted((a, b) => a.fromStageIndex - b.fromStageIndex);

    useEffect(() => {
        setType(typeOptions[0]?.value || '');
    }, [typeOptions]);

    // Add horizontal scrolling effect
    useEffect(() => {
        const scrollToElement = () => {
            // First try to scroll to single-product-selected
            const singleProductElement = document.getElementById('single-product-selected');
            if (singleProductElement) {
                singleProductElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
                return;
            }

            // If no single-product-selected, try to scroll to the first product-row
            const firstProductRow = document.querySelector('[id^="product-row-"]');
            if (firstProductRow) {
                firstProductRow.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        };

        // Add a small delay to ensure DOM elements are rendered
        const timeoutId = setTimeout(scrollToElement, 100);

        return () => clearTimeout(timeoutId);
    }, [filteredsimplifiedUseRecommendations, productId]);

    const firstSimplifiedUseRecommendation = filteredsimplifiedUseRecommendations[0];

    return (
        <Stack>
            {!initialCrop && (
                <Stack container py={5} spacing={3} direction='row'>
                    <MuiSelect
                        options={cropOptions}
                        inputLabel={_('Cultivo')}
                        value={crop}
                        onChange={(e) => setCrop(e.target.value as string)}
                    />
                    {typeOptions.length > 1 && (
                        <RadioButtons
                            options={typeOptions}
                            value={[type]}
                            onChange={(value) => {
                                setType(value);
                            }}
                            max={1}
                            theme={settings.app.theme}
                        />
                    )}
                </Stack>
            )}
            {cropStagesQuery.isValidating ? (
                <Stack width='100%' height='100%' justifyContent='center' alignItems='center'>
                    <Spinner />
                </Stack>
            ) : (
                <Stack overflow='scroll' width='100%'>
                    <Stack
                        direction='row'
                        divider={<Divider color='white' orientation='vertical' width='1px' />}
                        ml={'1px'}
                        position='relative'
                    >
                        {cropStages.map((cropStage, index) => (
                            <Stack key={_(cropStage.name)} mt='auto'>
                                <Box component='img' src={cropStage.image} width={WIDTH} />
                                <Stack
                                    alignItems='center'
                                    justifyContent='center'
                                    textAlign='center'
                                    py={0.5}
                                    sx={{
                                        background: (theme) => theme.palette.primary.main,
                                    }}
                                >
                                    <Typography
                                        variant='caption'
                                        width={`${WIDTH}px`}
                                        sx={{ color: 'white !important' }}
                                    >
                                        {_(cropStage.name)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ))}
                        {productId && firstSimplifiedUseRecommendation && (
                            <>
                                <Box
                                    height='100%'
                                    position='absolute'
                                    top={0}
                                    left={0}
                                    width={`${(WIDTH + 1) * firstSimplifiedUseRecommendation?.fromStageIndex - 1}px`}
                                    sx={{
                                        backdropFilter: 'saturate(0.1)',
                                    }}
                                />
                                <Box
                                    id='single-product-selected'
                                    height='100%'
                                    position='absolute'
                                    top={0}
                                    left={`${(WIDTH + 1) * firstSimplifiedUseRecommendation?.fromStageIndex - 1}px`}
                                    width={`${(WIDTH + 1) * (firstSimplifiedUseRecommendation?.toStageIndex - firstSimplifiedUseRecommendation?.fromStageIndex + 1)}px`}
                                    sx={{
                                        border: 1,
                                        borderColor: (theme) => theme.palette.primary.main,
                                        borderStyle: 'dashed',
                                    }}
                                />
                                <Box
                                    height='100%'
                                    position='absolute'
                                    top={0}
                                    left={`${(WIDTH + 1) * (firstSimplifiedUseRecommendation?.toStageIndex + 1)}px`}
                                    width={`${(WIDTH + 1) * (cropStages.length - firstSimplifiedUseRecommendation?.toStageIndex - 1)}px`}
                                    sx={{
                                        backdropFilter: 'saturate(0.1)',
                                    }}
                                />
                            </>
                        )}
                    </Stack>
                    {!productId && showCropProtectionProducts && (
                        <Stack spacing={2} mt={2} position='relative' pb={2}>
                            {simplifiedUseRecommendationsQuery.isValidating &&
                                [...Array(5)].map((_, index) => (
                                    <Skeleton variant='rectangular' key={index} width={'100%'} height={'82px'} />
                                ))}
                            {filteredsimplifiedUseRecommendations.map((useRecommendation) => (
                                <ProductRow useRecommendation={useRecommendation} key={useRecommendation.id} />
                            ))}
                            <Stack
                                direction='row'
                                position='absolute'
                                top={'-16px'}
                                left={0}
                                right={0}
                                bottom={0}
                                mt={'0 !important'}
                            >
                                {[...Array(cropStages.length + 1)].map((cropStage, index) => (
                                    <Divider
                                        orientation='vertical'
                                        flexItem
                                        key={index}
                                        sx={{
                                            borderStyle: 'dashed',
                                            mr: `${WIDTH}px`,
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
            {productId && (
                <Stack spacing={1} mt={2}>
                    {_(firstSimplifiedUseRecommendation?.note) && (
                        <Typography variant='body1' color='primary' textAlign='center'>
                            {_(firstSimplifiedUseRecommendation?.note)}
                        </Typography>
                    )}
                    {_(firstSimplifiedUseRecommendation?.alert) && (
                        <Alert severity='danger'>{_(firstSimplifiedUseRecommendation?.alert)}</Alert>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default CropProtectionWhenToApply;
